import { makeStyles, withStyles } from '@material-ui/core/styles';
import MuiTextField from '@material-ui/core/TextField';
import React, { useState } from 'react';
import styled from 'styled-components';
import { HIGHLIGHT, PLACEHOLDER, TEXT } from '../Colors';
import { BODY, HEADING_01, HEADING_02, HEADING_03, MINI_HEADER, SUBTITLE } from '../Fonts';
import { IconButton } from '../IconButton';
import { CROSS, EDIT, TICK } from '../Svgs';

const StyledTextField = withStyles({
    root: {
        marginTop: '2px',
        '& .MuiInput-input': { // Input
            color: TEXT,
            padding: 0
        },
        '& .MuiInput-input:focus': { // Input focused
            color: PLACEHOLDER
        },
        '& .MuiInput-underline.Mui-error:after': {
            borderBottom: `1px solid ${TEXT}`
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
            borderBottom: `2px solid ${TEXT}`
        },
        '& .MuiInput-underline.Mui-focused:hover:not(.Mui-disabled):before': {
            borderBottomWidth: '1px'
        },
        '& .MuiInput-underline:before': {
            borderBottomColor: TEXT
        },
        '& .MuiInput-underline:after': {
            borderBottom: `1px solid ${HIGHLIGHT}`
        }
    },
})(MuiTextField);

const useStyles = makeStyles({
    base: {
        color: TEXT,
    },
    body: {
        font: BODY,
        '&>.MuiTextField-root': {
            width: 'calc(100% - 16px - 2 * 24px)',
            verticalAlign: 'bottom',
            marginBottom: '1px',
            '&>.MuiInputBase-root': {
                font: BODY,
            }
        }
    },
    subtitle: {
        font: SUBTITLE,
        '&>.MuiTextField-root': {
            width: 'calc(100% - 16px - 2 * 24px)',
            verticalAlign: 'bottom',
            marginBottom: '1px',
            '&>.MuiInputBase-root': {
                font: SUBTITLE,
            }
        }
    },
    mini_header: {
        font: MINI_HEADER,
        '&>.MuiTextField-root': {
            width: 'calc(100% - 16px - 2 * 24px)',
            verticalAlign: 'bottom',
            marginBottom: '1px',
            '&>.MuiInputBase-root': {
                font: MINI_HEADER,
            }
        }
    },
    heading_01: {
        font: HEADING_01,
        '&>.MuiTextField-root': {
            width: 'calc(100% - 16px - 2 * 35px)',
            '&>.MuiInputBase-root': {
                font: HEADING_01,
            }
        }
    },
    heading_02: {
        font: HEADING_02,
        '&>.MuiTextField-root': {
            width: 'calc(100% - 16px - 2 * 31px)',
            '&>.MuiInputBase-root': {
                font: HEADING_02,
            }
        }
    },
    heading_03: {
        font: HEADING_03,
        '&>.MuiTextField-root': {
            width: 'calc(100% - 16px - 2 * 27px)',
            '&>.MuiInputBase-root': {
                font: HEADING_03,
            }
        }
    }
});

const StyledSpan = styled.span`
    margin-right: 6px;
`;

const StyledIconContainer = styled.div`
    display: inline-block;
    margin-left: 2px;
    width: ${props => props.theme.width};
    height: ${props => props.theme.height};
    &>* {
        vertical-align: bottom;
    }
`;

interface EditableLabelProps {
    value: string,
    typography: 'body' | 'subtitle' | 'mini_header' | 'heading_01' | 'heading_02' | 'heading_03',
    elementType?: 'span' | 'div',
    hidden?: boolean,
    onChange?(value: string): void,
    onStateChange?(isEditing: boolean): void
}

export const EditableLabel: React.FunctionComponent<EditableLabelProps> = (props) => {
    const classes = useStyles();

    // "stylingClassName" is used for "styled components" extensions. It is hidden from the developer.
    const stylingClassName = (props as any).className ? (props as any).className : '';

    const [isEditing, setIsEditing] = useState(false);
    const [editedValue, setEditedValue] = useState(props.value);

    const getSize = (): string => {
        switch (props.typography) {
            case "body":
                return "24px";
            case "subtitle":
                return "24px";
            case "mini_header":
                return "20px";
            case "heading_01":
                return "35px";
            case "heading_02":
                return "31px";
            case "heading_03":
                return "27px";
        }
    }

    const onEnableEdit = () => {
        setIsEditing(true);
        if (props.onStateChange) {
            props.onStateChange(true);
        }
    }

    const onAcceptEdit = () => {
        setIsEditing(false);
        if (props.onStateChange) {
            props.onStateChange(false);
        }
        if (props.onChange) {
            props.onChange(editedValue);
        }
    }

    const onCancelEdit = () => {
        setIsEditing(false);
        setEditedValue(props.value);
        if (props.onStateChange) {
            props.onStateChange(false);
        }
    }

    const className = `${classes.base} ${classes[props.typography]} ${stylingClassName}`;

    return (
        <div className={className} hidden={props.hidden}>
            {!isEditing && <StyledSpan>{props.value}</StyledSpan>}
            {!isEditing && <StyledIconContainer theme={{ width: getSize(), height: getSize() }}><IconButton type="secondary" fullHeight fullWidth onClick={() => onEnableEdit()}>{EDIT}</IconButton></StyledIconContainer>}

            {isEditing && <StyledTextField value={editedValue} onChange={ev => setEditedValue(ev.target.value)} />}
            {isEditing && <StyledIconContainer theme={{ width: getSize(), height: getSize() }}><IconButton fullHeight fullWidth type="secondary" onClick={() => onAcceptEdit()}>{TICK}</IconButton></StyledIconContainer>}
            {isEditing && <StyledIconContainer theme={{ width: getSize(), height: getSize() }}><IconButton fullHeight fullWidth type="secondary" onClick={() => onCancelEdit()}>{CROSS}</IconButton></StyledIconContainer>}
        </div>
    );
}